import { useState, useEffect, useCallback } from 'react';
import { accountsService, Account, AccountTransaction } from '../services/accountsService';
import { useAuth } from '../context/AuthContext';

export const useAccounts = () => {
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { accounts: data, error: fetchError } = await accountsService.getAccounts(businessId || undefined);

      if (fetchError) {
        throw fetchError;
      }

      setAccounts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    // Only fetch accounts when authentication is complete and user is authenticated
    if (!authLoading && isAuthenticated) {
      fetchAccounts();
    } else if (!authLoading && !isAuthenticated) {
      // Clear data if not authenticated
      setAccounts([]);
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, fetchAccounts]);

  const refreshAccounts = () => {
    fetchAccounts();
  };

  return {
    accounts,
    loading,
    error,
    refreshAccounts,
  };
};

export const useAccount = (id: string | null) => {
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    // Only fetch account when authentication is complete and user is authenticated
    if (!authLoading && isAuthenticated) {
      const fetchAccount = async () => {
        try {
          setLoading(true);
          setError(null);
          const { account: data, error: fetchError } = await accountsService.getAccountById(id, businessId || undefined);

          if (fetchError) {
            throw fetchError;
          }

          setAccount(data);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch account');
        } finally {
          setLoading(false);
        }
      };

      fetchAccount();
    } else if (!authLoading && !isAuthenticated) {
      // Clear data if not authenticated
      setAccount(null);
      setLoading(false);
    }
  }, [id, authLoading, isAuthenticated, businessId]);

  return {
    account,
    loading,
    error,
  };
};

export const useAccountTransactions = (accountId: string | null) => {
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const [transactions, setTransactions] = useState<AccountTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId) {
      setLoading(false);
      return;
    }

    // Only fetch transactions when authentication is complete and user is authenticated
    if (!authLoading && isAuthenticated) {
      const fetchTransactions = async () => {
        try {
          setLoading(true);
          setError(null);
          const { transactions: data, error: fetchError } = await accountsService.getAccountTransactions(accountId, businessId || undefined);

          if (fetchError) {
            throw fetchError;
          }

          setTransactions(data);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch transactions');
        } finally {
          setLoading(false);
        }
      };

      fetchTransactions();
    } else if (!authLoading && !isAuthenticated) {
      // Clear data if not authenticated
      setTransactions([]);
      setLoading(false);
    }
  }, [accountId, authLoading, isAuthenticated, businessId]);

  return {
    transactions,
    loading,
    error,
  };
};